import React, { useState } from "react";
import Card from "../../components/Card";
import Action from "../../components/Action";
import RadioConditional from "../../components/RadioConditional";
import RadioQuestion from "../../components/RadioQuestion";
import TextQuestion from "../../components/TextQuestion";
import ScrollToTop from "../../components/utils/ScrollToTop";
import { MORTGAGE_LENDER_OPTIONS, YES_NO_OPTIONS } from "../../constants";
import Image from "../../components/Image";
import imageCatAndHousePlant from "../../images/cat-and-houseplant.png";
import { isMoneyValid, normaliseMoney } from "../../shared/utils/moneyUtils";
import { isEmailValid } from "../../shared/utils/validation";

let buyingMortgageLenders = [...MORTGAGE_LENDER_OPTIONS];
// Remove the last item 'Other' from the list
buyingMortgageLenders.pop();

const defaultErrorMessages = {
  estimateMortgageAmount: "Please fill this in",
  isBuyerUsingMortgageBroker: "Please choose an option",
  mortgageBrokerFullName: "Please fill this in",
  mortgageBrokerEmailAddress: "Please enter an email address",
  isMortgageLenderKnown: "Please choose an option",
  mortgageLenderChosen: "Please choose an option",
};

const Mortgage = ({ mortgage, onStepComplete }) => {
  const [formState, setFormState] = useState({
    estimateMortgageAmount: mortgage?.estimateMortgageAmount || "",
    isBuyerUsingMortgageBroker: mortgage?.isBuyerUsingMortgageBroker ?? null,
    mortgageBrokerFullName: mortgage?.mortgageBrokerFullName || "",
    mortgageBrokerEmailAddress: mortgage?.mortgageBrokerEmailAddress || "",
    mortgageBrokerPhoneNumber: mortgage?.mortgageBrokerPhoneNumber || "",
    isMortgageLenderKnown: mortgage?.isMortgageLenderKnown ?? null,
    mortgageLenderChosen: mortgage?.mortgageLenderChosen || "",
  });
  const [errors, setErrors] = useState({});
  const [errorMessages, setErrorMessages] = useState({ ...defaultErrorMessages });

  const handleInputChange = (field) => (value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleValidation = () => {
    let newErrors = {};
    let newErrorMessages = { ...errorMessages };
    let formIsValid = true;

    if (!formState.estimateMortgageAmount) {
      newErrors.estimateMortgageAmount = true;
      formIsValid = false;
    }

    if (formState.estimateMortgageAmount !== "" && !isMoneyValid(formState.estimateMortgageAmount)) {
      newErrors.estimateMortgageAmount = true;
      newErrorMessages.estimateMortgageAmount = "Please enter a valid amount";
      formIsValid = false;
    }

    if (formState.isBuyerUsingMortgageBroker === null) {
      newErrors.isBuyerUsingMortgageBroker = true;
      formIsValid = false;
    }

    if (formState.isBuyerUsingMortgageBroker === true) {
      if (!formState.mortgageBrokerFullName) {
        newErrors.mortgageBrokerFullName = true;
        formIsValid = false;
      }

      if (!formState.mortgageBrokerEmailAddress) {
        newErrors.mortgageBrokerEmailAddress = true;
        newErrorMessages.mortgageBrokerEmailAddress = "Please enter an email address";
        formIsValid = false;
      }
      if (formState.mortgageBrokerEmailAddress !== "" && !isEmailValid(formState.mortgageBrokerEmailAddress)) {
        newErrors.mortgageBrokerEmailAddress = true;
        newErrorMessages.mortgageBrokerEmailAddress = "Please enter a valid email address";
        formIsValid = false;
      }
    }

    if (formState.isMortgageLenderKnown === null) {
      newErrors.isMortgageLenderKnown = true;
      formIsValid = false;
    }

    if (formState.isMortgageLenderKnown === true) {
      if (!formState.mortgageLenderChosen) {
        newErrors.mortgageLenderChosen = true;
        formIsValid = false;
      }
    }

    setErrors(newErrors);
    setErrorMessages(newErrorMessages);
    return formIsValid;
  };

  const handleFieldValidation = (field) => (event) => {
    const isBlur = event.type === "blur";
    setErrors((prev) => {
      let newErrors = { ...prev };

      if (!isBlur) {
        newErrors[field] = false;
      } else {
        if (!formState[field]) {
          newErrors[field] = true;
        }
      }

      return newErrors;
    });
  };

  const handleRadioValidation = (field) => (event) => {
    const isBlur = event.type === "blur";
    setErrors((prev) => {
      let newErrors = { ...prev };

      if (!isBlur) {
        newErrors[field] = false;
      } else {
        if (formState[field] === null) {
          newErrors[field] = true;
        }
      }

      return newErrors;
    });
  };

  const handleEmailAddressValidation = (field) => (event) => {
    const isBlur = event.type === "blur";
    setErrors((prev) => {
      let newErrors = { ...prev };

      if (!isBlur) {
        newErrors[field] = false;
      } else {
        if (!formState[field]) {
          newErrors[field] = true;
        } else if (!isEmailValid(formState[field])) {
          newErrors[field] = true;
        }
      }

      return newErrors;
    });
    setErrorMessages((prev) => {
      let newErrorMessages = { ...prev };

      if (isBlur) {
        if (!formState[field]) {
          newErrorMessages[field] = "Please enter an email address";
        } else if (!isEmailValid(formState[field])) {
          newErrorMessages[field] = "Please enter a valid email address";
        }
      }

      return newErrorMessages;
    });
  };

  const handleMoneyValidation = (field) => (event) => {
    const isBlur = event.type === "blur";
    setErrors((prev) => {
      let newErrors = { ...prev };

      if (!isBlur) {
        newErrors[field] = false;
      } else {
        if (!formState[field]) {
          newErrors[field] = true;
        } else if (!isMoneyValid(formState[field])) {
          newErrors[field] = true;
        }
      }

      return newErrors;
    });
    setErrorMessages((prev) => {
      let newErrorMessages = { ...prev };

      if (isBlur) {
        if (!formState[field]) {
          newErrorMessages[field] = "Please fill this in";
        } else if (!isMoneyValid(formState[field])) {
          newErrorMessages[field] = "Please enter a valid amount";
        }
      }

      return newErrorMessages;
    });
  };

  const saveMortgage = (event) => {
    event.preventDefault();

    if (handleValidation()) {
      onStepComplete({
        mortgage: {
          estimateMortgageAmount: normaliseMoney(formState.estimateMortgageAmount),
          isBuyerUsingMortgageBroker: formState.isBuyerUsingMortgageBroker,
          mortgageBrokerFullName: formState.mortgageBrokerFullName,
          mortgageBrokerEmailAddress: formState.mortgageBrokerEmailAddress,
          mortgageBrokerPhoneNumber: formState.mortgageBrokerPhoneNumber,
          isMortgageLenderKnown: formState.isMortgageLenderKnown,
          mortgageLenderChosen: formState.mortgageLenderChosen,
        },
      });
    }
  };

  return (
    <React.Fragment>
      <ScrollToTop />
      <Card title="Tell us about your mortgage" removeTopPadding>
        <p>We'll look through the full offer soon, so don't worry if these details change</p>

        <TextQuestion
          question="How much will you be borrowing for your mortgage?"
          hint="If you don’t know an exact figure yet, enter an estimated mortgage amount"
          value={formState.estimateMortgageAmount}
          valueEntered={handleInputChange("estimateMortgageAmount")}
          validate={handleMoneyValidation("estimateMortgageAmount")}
          validationError={errors.estimateMortgageAmount ? errorMessages.estimateMortgageAmount : null}
        />

        <RadioQuestion
          question="Are you using a mortgage broker?"
          options={YES_NO_OPTIONS}
          inline={true}
          name="isBuyerUsingMortgageBroker"
          selected={formState.isBuyerUsingMortgageBroker}
          optionSelected={handleInputChange("isBuyerUsingMortgageBroker")}
          validate={handleRadioValidation("isBuyerUsingMortgageBroker")}
          validationError={errors.isBuyerUsingMortgageBroker ? errorMessages.isBuyerUsingMortgageBroker : null}
        />

        {formState.isBuyerUsingMortgageBroker === true && (
          <RadioConditional>
            <TextQuestion
              question="What is your broker’s name?"
              value={formState.mortgageBrokerFullName}
              valueEntered={handleInputChange("mortgageBrokerFullName")}
              validate={handleFieldValidation("mortgageBrokerFullName")}
              validationError={errors.mortgageBrokerFullName ? errorMessages.mortgageBrokerFullName : null}
            />

            <TextQuestion
              question="What is your broker’s email address?"
              value={formState.mortgageBrokerEmailAddress}
              valueEntered={handleInputChange("mortgageBrokerEmailAddress")}
              validate={handleEmailAddressValidation("mortgageBrokerEmailAddress")}
              validationError={errors.mortgageBrokerEmailAddress ? errorMessages.mortgageBrokerEmailAddress : null}
            />

            <TextQuestion
              question="What is your broker’s phone number?"
              value={formState.mortgageBrokerPhoneNumber}
              valueEntered={handleInputChange("mortgageBrokerPhoneNumber")}
            />
          </RadioConditional>
        )}

        <RadioQuestion
          question="Do you know who your lender will be yet?"
          options={YES_NO_OPTIONS}
          inline={true}
          name="isMortgageLenderKnown"
          selected={formState.isMortgageLenderKnown}
          optionSelected={handleInputChange("isMortgageLenderKnown")}
          validate={handleFieldValidation("isMortgageLenderKnown")}
          validationError={errors.isMortgageLenderKnown ? errorMessages.isMortgageLenderKnown : null}
        />

        {formState.isMortgageLenderKnown === true && (
          <RadioConditional>
            <RadioQuestion
              question="We can only work with lenders from the following list.
              Who will your mortgage be with?"
              options={buyingMortgageLenders}
              inline={false}
              name="mortgageLenderChosen"
              selected={formState.mortgageLenderChosen}
              optionSelected={handleInputChange("mortgageLenderChosen")}
              validate={handleFieldValidation("mortgageLenderChosen")}
              validationError={errors.mortgageLenderChosen ? errorMessages.mortgageLenderChosen : null}
            />
          </RadioConditional>
        )}

        <Action onClick={saveMortgage}>Continue</Action>
      </Card>
      <Image src={imageCatAndHousePlant} />
    </React.Fragment>
  );
};

export default Mortgage;
